import { FilterOption, IrdPropertyType } from '../property';
import { AS_JSON, AS_YES_NO } from './formats';
import { IRD_DEFAULT_EMPTY_MARKER, IrdFormat } from './resolved';

/**
 * What a value is formatted against: a resolved property, or a legacy `IrdProperty` the generic
 * table still reads — both carry a type, and only the resolved one declares a format and marker.
 */
export interface IrdFormattable {
  readonly type: IrdPropertyType;
  readonly format?: IrdFormat;
  readonly emptyMarker?: string;
  readonly allowedValues?: readonly FilterOption[];
}

/** The locale- and language-dependent steps, which the framework supplies (`IrdFormatService`). */
export interface IrdValueFormatters {
  date(value: Date | string | number, dateFormat: string): string | null;
  number(value: number, digitsInfo: string): string | null;
  translate(key: string): string;
}

const AS_TEXT: IrdFormat = { kind: 'text' };

/**
 * How a value reads when its property declares no format.
 *
 * Keyed by the full property-type union on purpose: a new type does not compile until someone
 * decides how it reads, rather than rendering unformatted.
 */
export const IRD_PROPERTY_TYPE_DEFAULTS: Readonly<Record<IrdPropertyType, IrdFormat>> = {
  string: AS_TEXT,
  number: { kind: 'number' },
  boolean: AS_YES_NO,
  Date: { kind: 'date' },
  // An object has no text form of its own; it declares a format or reads as the empty marker.
  Object: AS_TEXT,
  // Without allowed values the identifier is all there is; with them it reads as their label.
  select: AS_TEXT,
  enum: AS_TEXT,
  // Their markup is the value renderer's job; as text, they read as their value.
  icon: AS_TEXT,
  color: AS_TEXT,
  json: AS_JSON,
};

/**
 * The one value-to-string path. Cell, tooltip and detail view all read this, so they show the
 * same string.
 *
 * Framework-free: the locale- and language-dependent steps are handed in once, through the
 * constructor, by `IrdFormatService` in `@ird/ng-core`.
 *
 * An absent value reads as the empty marker, and so does a value its format cannot render: a
 * throwing format is contained to its own value and logged, never propagated — one bad row must
 * not take the page down, and a silently swallowed error is a defect that lives forever.
 */
export class IrdValueFormatter {
  /** Grouped, with at most two decimals and none forced: 1234.567 reads 1,234.57 and 3 reads 3. */
  private static readonly DEFAULT_DIGITS_INFO = '1.0-2';
  private static readonly DEFAULT_DATE_FORMAT = 'shortDate';

  constructor(private readonly formatters: IrdValueFormatters) {}

  /**
   * `0` and `false` are values; an empty string, array or plain object is not. Any other object —
   * a `Date`, a class instance — carries its value somewhere other than its own keys.
   */
  public static hasDisplayValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }
    if (IrdValueFormatter.isPlainObject(value)) {
      return Object.keys(value).length > 0;
    }
    return true;
  }

  /** `String({})` is `'[object Object]'`, which reads like a value and is not one. */
  private static asText(value: unknown): string | null {
    if (Array.isArray(value)) {
      return value
        .map((item) => IrdValueFormatter.asText(item))
        .filter(Boolean)
        .join(', ');
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return value.toString();
    }
    return null;
  }

  private static isPlainObject(value: unknown): value is object {
    if (typeof value !== 'object' || value === null) {
      return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  }

  public format(value: unknown, property: IrdFormattable): string {
    const emptyMarker = property.emptyMarker ?? IRD_DEFAULT_EMPTY_MARKER;
    if (!IrdValueFormatter.hasDisplayValue(value)) {
      return emptyMarker;
    }
    try {
      if (!property.format && (property.type === 'enum' || property.type === 'select') && property.allowedValues?.length) {
        return this.allowedValueLabel(value, property.allowedValues) || emptyMarker;
      }
      // `||`, not `??`: a format that renders an empty string reads as the empty marker too.
      return this.render(property.format ?? IRD_PROPERTY_TYPE_DEFAULTS[property.type], value, property.type) || emptyMarker;
    } catch (error) {
      console.error(`IrdValueFormatter: a '${property.type}' value could not be formatted`, error);
      return emptyMarker;
    }
  }

  /** The translated label of an allowed value, else the value itself. A list reads as its items' labels. */
  private allowedValueLabel(value: unknown, allowedValues: readonly FilterOption[]): string {
    if (Array.isArray(value)) {
      return value
        .filter((item) => IrdValueFormatter.hasDisplayValue(item))
        .map((item) => this.allowedValueLabel(item, allowedValues))
        .filter(Boolean)
        .join(', ');
    }
    const text = IrdValueFormatter.asText(value) ?? '';
    const label = allowedValues.find((option) => option.value === text)?.label;
    return label ? this.formatters.translate(label) : text;
  }

  private render(format: IrdFormat, value: unknown, type: IrdPropertyType): string | null {
    switch (format.kind) {
      case 'text':
        return IrdValueFormatter.asText(value);
      case 'number':
        // Only a real number is grouped; anything else shows as it is, so a mis-declared column
        // neither turns `true` into `1` nor drops the leading zeros of "007".
        return typeof value === 'number'
          ? this.formatters.number(value, format.digitsInfo ?? IrdValueFormatter.DEFAULT_DIGITS_INFO)
          : IrdValueFormatter.asText(value);
      case 'date':
        return this.formatters.date(value as Date | string | number, format.dateFormat ?? IrdValueFormatter.DEFAULT_DATE_FORMAT);
      case 'list': {
        const items = Array.isArray(value) ? value : [value];
        const itemFormat = IRD_PROPERTY_TYPE_DEFAULTS[type];
        return items
          .filter((item) => IrdValueFormatter.hasDisplayValue(item))
          .map((item) => this.render(itemFormat, item, type))
          .filter(Boolean)
          .join(format.separator);
      }
      case 'custom':
        return format.render(value);
      case 'translation-key':
        return this.formatters.translate(format.render(value));
    }
  }
}
